import compact from "lodash-es/compact";
import uniq from "lodash-es/uniq";
import type { ApiJob } from "@tradingdroid/frontends-types";
import { tdApi } from "@src/services/api/tdApi";
import { isGroupCacheInFlightRefusal } from "@src/utils/apiError";
import { delay } from "@src/utils/dateUtils";

const GROUP_CACHE_WAIT_MS = 30000;
const GROUP_CACHE_POLL_MS = 3000;
// The read cap bounds the loop if the wall clock steps back.
const MAX_GROUP_CACHE_READS = GROUP_CACHE_WAIT_MS / GROUP_CACHE_POLL_MS + 1;
const JOB_READ_UNAVAILABLE = Symbol("job read unavailable");

export class GroupCacheBusyError extends Error {
  constructor(groupIds: number[]) {
    const groups = groupIds.map((id) => `#${id}`).join(", ");
    super(
      groupIds.length > 1
        ? `Execution groups ${groups} are still building their caches, so their membership cannot change yet. Try again once the group caches finish.`
        : groups
          ? `Execution group ${groups} is still building its cache, so its membership cannot change yet. Try again once the group cache finishes.`
          : "An execution group this change affects is still building its cache, so the membership cannot change yet. Try again once the group cache finishes.",
    );
    this.name = "GroupCacheBusyError";
  }
}

export class GroupMembershipChangeAbandonedError extends Error {
  constructor() {
    super("The execution group membership change is no longer requested.");
    this.name = "GroupMembershipChangeAbandonedError";
  }
}

/**
 * Waits for active cache jobs targeting groupIds after a 409 with reason execution_group_cache_in_flight.
 * Other change errors are rethrown. Calls change at most twice.
 * During the wait, checks isStillWanted after job reads and sleeps. Once false, it must stay false.
 */
export async function runGroupMembershipChange<T>(
  change: () => Promise<T>,
  groupIds: Array<number | null | undefined>,
  onWaitingForGroupCache?: () => void,
  isStillWanted: () => boolean = () => true,
): Promise<T> {
  try {
    return await change();
  } catch (error) {
    if (!isGroupCacheInFlightRefusal(error)) throw error;
  }

  const deadline = Date.now() + GROUP_CACHE_WAIT_MS;
  onWaitingForGroupCache?.();
  const knownGroupIds = uniq(compact(groupIds));
  let lastActiveGroupIds: number[] = [];
  let settled = false;
  for (let read = 0; read < MAX_GROUP_CACHE_READS; read++) {
    if (!isStillWanted()) throw new GroupMembershipChangeAbandonedError();
    const remainingMs = deadline - Date.now();
    if (remainingMs <= 0) break;
    let deadlineTimer: ReturnType<typeof setTimeout> | undefined;
    let jobs;
    try {
      // A read in flight is not aborted when the deadline expires.
      jobs = await Promise.race([
        tdApi
          .getItemsWithQuery<ApiJob>(
            "v1/jobs",
            { q: "job_type_name.EQ:'cache' AND status_name.IN:'queued,claimed,running'" },
            { limit: 1000 },
            true,
          )
          .catch(() => JOB_READ_UNAVAILABLE),
        new Promise<typeof JOB_READ_UNAVAILABLE>((resolve) => {
          deadlineTimer = setTimeout(() => resolve(JOB_READ_UNAVAILABLE), remainingMs);
        }),
      ]);
    } finally {
      clearTimeout(deadlineTimer);
    }
    if (!isStillWanted()) throw new GroupMembershipChangeAbandonedError();
    if (!Array.isArray(jobs) || Date.now() >= deadline) break;
    lastActiveGroupIds = uniq(
      jobs.flatMap((job) => {
        if (job.job_type_name !== "cache") return [];
        const groupId = Number(job.config?.execution_group_id);
        return knownGroupIds.includes(groupId) ? [groupId] : [];
      }),
    );
    if (lastActiveGroupIds.length === 0) {
      settled = true;
      break;
    }
    if (Date.now() + GROUP_CACHE_POLL_MS > deadline) break;
    if (read < MAX_GROUP_CACHE_READS - 1) await delay(GROUP_CACHE_POLL_MS);
  }
  if (!settled) throw new GroupCacheBusyError(lastActiveGroupIds);

  if (!isStillWanted()) throw new GroupMembershipChangeAbandonedError();
  try {
    return await change();
  } catch (error) {
    if (isGroupCacheInFlightRefusal(error)) throw new GroupCacheBusyError([]);
    throw error;
  }
}
